<template>
  <div class="p-3">
    <!-- Info Panel -->
    <div class="bg-blue-50 mb-5 animate-fade-in">
      <div class="flex items-start">
        <h4>Add Anchor Elements</h4>
        <div class="p-3 anchor-info">
          <p class="text-sm text-blue-800 mb-1" style="font-weight:500; color:dodgerblue;">
            <md-icon class="text-primary mr-1" style="font-size: 18px">info</md-icon> About Anchor Elements
          </p>
          <div class="ml-4 text-blue-700">
            <p class="mb-0" style="color:cornflowerblue;">
              Anchor elements are specific visual details that should be included in the generated image. Select 3-5
              elements that are most important for your training scenario. Your precision score will be calculated based
              on anchors selection.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Search Input -->
    <div class="mb-5">
      <label for="search" class="block text-gray-700 text-sm font-semibold mb-2">Search Anchor Elements</label>
      <div class="relative rounded-md shadow-sm">
        <b-form-input
          class="w-lg-auto"
          name="search"
          id="search"
          placeholder="Search for anchor elements..."
          v-model="searchQuery"
          @input="onSearch"
        />
      </div>
    </div>

    <!-- Selected Anchors -->
    <div class="mb-2">
      <div class="anchor-count">
        <h5 class="text-lg font-medium text-gray-900 mb-0">Selected Anchor Elements</h5>
        <span class="selected-count px-3 py-1"> {{ selectedAnchors.length }} selected </span>
      </div>

      <div v-if="selectedAnchors.length" class="selected-anchors mt-2 flex flex-wrap gap-2">
        <div
          v-for="(anchor, index) in selectedAnchors"
          :key="index"
          class="selected-anchor-item bg-white border border-gray-200 rounded-full px- py-1 "
        >
          <span class="text-sm" @click="toggleSelection(anchor)">
            <md-icon style="font-size: 18px">close</md-icon>
            {{ anchor.length > 20 ? anchor.slice(0, 20) + '...' : anchor }}</span
          >
        </div>
      </div>
    </div>

    <!-- Available Elements Section -->
    <div class="space-y-6">
      <div class="animate-slide-in" style="animation-delay: 0.1s;">
        <div class="mb-3">
          <span class="badge" style="background-color: #dbeafe; color: #1e40af; padding: 6px 12px; border-radius: 20px; font-size: 14px; font-weight: 500;">
            {{ skillstep }}
          </span>
        </div>

        <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div v-if="anchorElements.length !== 0">
            <div
              v-for="(element, elementIndex) in filteredAnchorElements"
              :key="elementIndex"
              class="p-2 border-gray-200  anchor-list-item hover:bg-gray-50 transition-colors"
            >
              <div class="flex items-center">
                <div class="flex-shrink-0">
                  <div class="w-6 py-2 px-2 rounded flex items-center justify-center cursor-pointer">
                    <b-form-checkbox
                      :aria-checked="isSelected(element)"
                      name="anchors"
                      v-model="selectedAnchors"
                      :value="element"
                    >
                      <div class="d-flex" style="cursor: pointer">
                        <span style="font-size: 16px">
                          {{ element }}
                        </span>
                      </div>
                    </b-form-checkbox>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else>
            <div class="p-4 text-center text-gray-500" style="font-style: italic;">
              <i v-if="isAnchorLoading" class="fas fa-circle-notch fa-spin" style="font-size: large; color: gray;"></i>
              <p v-if="!isAnchorLoading" class="mb-0">No anchor elements found.</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Navigation Buttons -->
    <div class="nav-buttons mt-8 flex justify-between">
      <div class="mt-6 w-full" :class="isMobileScreen ? 'mb-5' : ''" style="justify-self: end;">
        <b-btn class="border" variant="ghost" @click="goToPrevTab">
          BACK
        </b-btn>
      </div>

      <div class="mt-6 w-full" :class="isMobileScreen ? 'mb-5' : ''" style="justify-self: end;">
        <b-btn variant="primary" @click="goToNextTab">
          Next: Generate Image
          <i class="material-icons ml-1">flash_on</i>
        </b-btn>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { get } from 'lodash';

export default {
  name: 'AnchorElementsForm',
  props: {
    onNext: {
      type: Function,
      required: true,
    },
    myIndex: Number,
    activeTabIndex: Number,
  },
  data() {
    return {
      anchorElements: [],
      selectedAnchors: [],
      searchQuery: '',
      skillmap: '',
      skillstep: '',
      windowWidth: window.innerWidth,
      isAnchorLoading: false,
    };
  },
  computed: {
    ...mapGetters('aiSkills', ['getSkillCard']),
    ...mapGetters('auth', ['getLoggedInUser']),
    filteredAnchorElements() {
      if (!this.searchQuery) return this.anchorElements;

      return this.anchorElements.filter(element => element.toLowerCase().includes(this.searchQuery.toLowerCase()));
    },
    isMobileScreen() {
      return this.windowWidth <= 420;
    },
  },
  watch: {
    activeTabIndex: {
      immediate: true,
      handler() {
        this.isActive = this.activeTabIndex === this.myIndex;
        if (this.isActive) {
          this.skillstep = this.getSkillCard.step_name;
          this.selectedAnchors = [];
          this.fetchAnchorElements();
        }
      },
    },
  },
  methods: {
    ...mapActions('aiSkills', ['setSkillCardValues', 'getSkillStepAnchors']),
    get,
    isSelected(element) {
      return this.selectedAnchors.includes(element);
    },
    onSearch() {},
    // Toggle selection of an element
    toggleSelection(element) {
      if (this.isSelected(element)) {
        this.selectedAnchors = this.selectedAnchors.filter(e => e !== element);
      } else {
        this.selectedAnchors.push(element);
      }
    },
    goToPrevTab() {
      this.onNext(1);
    },
    goToNextTab() {
      this.setSkillCardValues({
        ...this.getSkillCard,
        anchorElements: this.selectedAnchors,
      });
      this.onNext(3);
    },
    async fetchAnchorElements() {
      try {
        this.isAnchorLoading = true;
        const resp = await this.getSkillStepAnchors({ skill_step: this.getSkillCard.step });
        this.anchorElements = resp.data ? resp.data : [];
        this.isAnchorLoading = false;
      } catch (error) {
        this.isAnchorLoading = false;
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },
  },
};
</script>
<style scoped>
.anchor-info {
  background-color: rgb(239 246 255 / var(--tw-bg-opacity, 1));
  border-radius: 8px;
  font-size: medium;
}
.anchor-count {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.selected-count {
  background-color: rgb(241 245 249 / var(--tw-bg-opacity, 1));
  border-radius: 20px;
  font-size: small;
  font-weight: bold;
}
.selected-anchors {
  display: flex;
  background-color: rgb(241 245 249 / var(--tw-bg-opacity, 1));
  border-radius: 10px;
  padding: 10px;
}
.selected-anchor-item {
  background-color: rgb(255 255 255 / var(--tw-bg-opacity, 1));
  border: 1px solid rgb(209 213 219 / var(--tw-border-opacity, 1));
  border-radius: 20px;
  padding: 5px 10px;
  margin-right: 10px;
  display: flex;
  align-items: center;
}
.skill-step-tag {
  background-color: rgb(209 250 229 / var(--tw-bg-opacity, 1));
  color: white;
  font-weight: 500;
  padding: 5px 10px;
  border-radius: 9999px;
}
.anchor-list-item {
  border-bottom: 1px solid rgb(229 231 235 / var(--tw-border-opacity, 1));
}
.anchor-list-item:last-child {
  border-bottom: none;
}
.nav-buttons {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
}
</style>
